import appConfig from 'staff-app-config';

import { getThemeVariant } from './theme-variant';
import * as legacy from './legacy-variables';
import { buildReskinColorPalette } from './reskin-palette';

// Color palette names follow design system tokens.
// Off-palette colors (pending design decision) are grouped at the bottom — see GH issue #23244.
//
// POC #33148: values swapped to @ourbranch/ui-core's palette (packages/ui-core/src/variables.ts).
// Token NAMES are unchanged so the 407 style files keep working; only the hex values moved.
// Numeric suffixes no longer imply lightness, and some pairs now share a ui-core value.
// Hex is copied rather than imported because ui-core/src/utils.ts pulls MUI v5 in at module
// scope, which would drag Emotion into staff-fe's v4 bundle. See the proposal's "Palette sync".
const branchReskinColorPalette = {
  // ---- Light ---- (ui-core white50 / beige50 / beige100 / beige150 / beige200 / beige700 / beige900)
  white: '#FFFFFF',
  white_op_10: 'rgba(255, 255, 255, 0.10)',
  white_op_15: 'rgba(255, 255, 255, 0.15)',
  white_op_30: 'rgba(255, 255, 255, 0.3)',
  beige_25: '#F8F6F2',
  beige_25_op_30: 'rgba(248, 246, 242, 0.3)',
  beige_40: '#F3F0ED',
  beige_50: '#F1ECE8',
  beige_50_op_13: 'rgba(241, 236, 232, 0.13)',
  beige_50_op_50: 'rgba(241, 236, 232, 0.5)',
  beige_50_op_60: 'rgba(241, 236, 232, 0.60)',
  beige_50_op_70: 'rgba(241, 236, 232, 0.7)',
  beige_50_op_75: 'rgba(241, 236, 232, 0.75)',
  beige_60: '#DFD8D2',
  // Secondary button fill, hover and pressed: translucent so the button reads on white and beige panels.
  beige_60_op_30: 'rgba(223, 216, 210, 0.3)',
  beige_60_op_55: 'rgba(223, 216, 210, 0.55)',
  beige_60_op_80: 'rgba(223, 216, 210, 0.8)',
  beige_75: '#B4AB99',
  beige_90: '#706553',

  // ---- Dark (Green) ---- (ui-core green100 / green150 / green200 / green600)
  green_50: '#527059',
  green_75: '#527059',
  green_75_op_10: 'rgba(82, 112, 89, 0.10)',
  green_75_op_30: 'rgba(82, 112, 89, 0.3)',
  green_75_op_40: 'rgba(82, 112, 89, 0.4)',
  green_75_op_60: 'rgba(82, 112, 89, 0.60)',
  green_75_op_75: 'rgba(82, 112, 89, 0.75)',
  green_75_10: '#EDF0EE',
  green_80: '#343B1B',
  green_90: '#343B1B',
  green_90_op_80: 'rgba(52, 59, 27, 0.8)',
  green_100: '#0B1F15',

  // ---- Accent (Bark) ---- (ui-core bark500 / bark100 / bark50)
  orange_50: '#C93800',
  orange_50_op_08: 'rgba(201, 56, 0, 0.08)',
  orange_50_op_10: 'rgba(201, 56, 0, 0.1)',
  orange_50_op_50: 'rgba(201, 56, 0, 0.5)',
  orange_50_op_70: 'rgba(201, 56, 0, 0.7)',
  orange_75: '#EF4E10',
  // ui-core bark50. Accent text on dark-green surfaces: bark500 only reaches 2.27:1 there,
  // where the old amber passed. Use this wherever the accent sits on green_75/green_90/green_100.
  orange_on_dark: '#FFCEBC',
  orange_40: 'rgba(201, 56, 0, 0.4)',

  // ---- Error (Red) ---- (ui-core red50 / red10 / red100)
  red_50: '#7A0000',
  red_50_op_10: 'rgba(122, 0, 0, 0.1)',
  red_100: '#7A0000',
  red_100_10: '#FBEBE9',

  // ---- Off-palette (pending design decision; see #23244) ----
  green_legacy_86c440: '#527059',
  grey_10_op_38: 'rgba(52, 59, 27, 0.38)',

  // ---- Folded in from the semantic `colors` map (POC #33148) ----
  // These previously bypassed the palette as raw hex literals in `colors` below.
  green_success: '#527059',
  green_success_light: '#B4C7B8',
  green_success_dark: '#0B1F15',
  green_success_dark_op_90: 'rgba(11, 31, 21, 0.9)',
  red_error: '#7A0000',
  red_error_light: '#FBEBE9',
  red_error_dark: '#3D0202',
  red_error_dark_op_90: 'rgba(61, 2, 2, 0.9)',
  black: '#000000',
  black_op_87: 'rgba(0, 0, 0, 0.87)',
  black_op_70: 'rgba(0, 0, 0, 0.70)',
  gray_page: '#F8F6F2',
  gray_01: '#F3F0ED',
  gray_02: '#F8F6F2',
  gray_03: '#757774',
  gray_04: '#343B1B'
};

// Other brands set seed colors in their staff config (`reskinPalette`); Branch keeps the values above.
const reskinColorPalette = appConfig.reskinPalette
  ? buildReskinColorPalette(appConfig.reskinPalette)
  : branchReskinColorPalette;

const reskinColors = {
  notice_background: reskinColorPalette.green_75,
  action_text: reskinColorPalette.green_100,
  // Two action colors, as in quote: bark for the main action and the selection controls, neutral for the rest.
  action_icon: reskinColorPalette.green_100,
  selection_control: reskinColorPalette.orange_50,
  badge_background: reskinColorPalette.beige_50,
  green: reskinColorPalette.green_success,
  green_light: reskinColorPalette.green_success_light,
  green_80: reskinColorPalette.green_80,
  green_dark: reskinColorPalette.green_success_dark,
  green_dark_90: reskinColorPalette.green_success_dark_op_90,
  red: reskinColorPalette.red_error,
  red_light: reskinColorPalette.red_error_light,
  red_dark: reskinColorPalette.red_error_dark,
  red_dark_90: reskinColorPalette.red_error_dark_op_90,
  black: reskinColorPalette.black,
  gray: reskinColorPalette.gray_page,
  white: reskinColorPalette.white,
  gray_01: reskinColorPalette.gray_01,
  gray_02: reskinColorPalette.gray_02,
  gray_03: reskinColorPalette.gray_03,
  gray_04: reskinColorPalette.gray_04,

  // page general styles
  page__title: reskinColorPalette.green_75,
  page__subtitle: reskinColorPalette.green_80,
  page__subtitle1: reskinColorPalette.green_90,
  page__background_primary: reskinColorPalette.white,
  // buttons
  button__background_primary: reskinColorPalette.green_100,
  button__background_secondary: reskinColorPalette.orange_50,
  button__text_primary: reskinColorPalette.white,

  // header
  header__background_primary: reskinColorPalette.white,
  header__label_primary: reskinColorPalette.green_90,
  header__label_secondary: reskinColorPalette.green_75,

  // footer
  footer_background_primary: reskinColorPalette.white,
  // card styles
  info__background_primary: reskinColorPalette.white,
  info__label_primary: reskinColorPalette.green_80,
  info__label_secondary: reskinColorPalette.green_75,
  info__label_title: reskinColorPalette.green_75,
  info__label_subtitle: reskinColorPalette.green_75,
  info__label_tertiary: reskinColorPalette.green_75,
  // Alert text in cards: excluded drivers, VIN discrepancies.
  info__label_alert: reskinColorPalette.orange_50,
  info__label_text: reskinColorPalette.orange_50,

  coverage__background_primary: reskinColorPalette.white,
  coverage__background_secondary: reskinColorPalette.beige_25,
  coverage__label_primary: reskinColorPalette.green_100,
  coverage__label_secondary: reskinColorPalette.green_75,
  coverage__label_subtitle: reskinColorPalette.green_100,
  coverage__label_title: reskinColorPalette.green_100,
  coverage__label_paragraph: reskinColorPalette.green_75,

  payment__background_primary: reskinColorPalette.green_75,
  payment__background_secondary: reskinColorPalette.beige_25,
  terms__background_primary: reskinColorPalette.beige_25,

  card_primary_separator: reskinColorPalette.beige_25,

  // form styles
  form__background_primary: reskinColorPalette.beige_25,
  modal__background_primary: reskinColorPalette.white,
  form_label_primary: reskinColorPalette.beige_50_op_70,
  form_border_primary: reskinColorPalette.beige_60,
  // plan picker styles
  planPicker_label_primary: reskinColorPalette.green_100,
  planPicker_label_secondary: reskinColorPalette.green_75,

  text_primary: reskinColorPalette.green_100,
  text_secondary: reskinColorPalette.green_75,
  text_disabled: reskinColorPalette.grey_10_op_38,
  text_secondary_background: reskinColorPalette.white,

  light_text: reskinColorPalette.green_75,
  medium_text: reskinColorPalette.beige_60,
  light_background: reskinColorPalette.white_op_10,

  card_background_primary: reskinColorPalette.white,
  // Add forms, coverage blocks and other inset panels inside page content.
  panel_background: reskinColorPalette.beige_25,
  // Section bodies are white inside their outlined card, like quote's; inset panels keep `panel_background`.
  card_background_primary_editable: reskinColorPalette.white,
  card_background_secondary: reskinColorPalette.white,
  card_border_secondary: reskinColorPalette.beige_25,
  card_background_highlight: reskinColorPalette.beige_25,
  card_divider: reskinColorPalette.beige_60,
  card_background_tertiary: reskinColorPalette.orange_75,
  card_background_modal: reskinColorPalette.beige_50,

  tooltip_background: reskinColorPalette.green_100,
  table_row_divider: reskinColorPalette.beige_60,
  section_label_secondary: reskinColorPalette.beige_90,
  preview_link: reskinColorPalette.orange_50,

  loading_background: reskinColorPalette.orange_40,
  loading_bar: reskinColorPalette.orange_50,

  body: reskinColorPalette.black_op_87,
  body_light: reskinColorPalette.black_op_70,
  error: reskinColorPalette.red_50,
  success: reskinColorPalette.green_legacy_86c440
};

// Content that sat on dark-green cards reads `theme.onCard.<palette name>`. Classic gets the palette
// unchanged; the new look's cards are light, so the light-on-dark colors map to their dark-on-light pair.
const P = reskinColorPalette;
const reskinOnCardPalette = {
  ...P,
  white: P.green_100,
  beige_50: P.green_75,
  beige_50_op_50: P.green_75,
  beige_50_op_60: P.green_75,
  beige_50_op_70: P.green_75,
  beige_50_op_75: P.green_75,
  beige_50_op_13: P.grey_10_op_38,
  beige_25_op_30: P.beige_60,
  white_op_30: P.beige_60,
  white_op_10: P.beige_25,
  white_op_15: P.beige_25,
  green_90: P.white,
  green_100: P.beige_25,
  orange_on_dark: P.orange_50,
  beige_25: P.green_100,
  green_50: P.green_75
};

// Dividers and borders drawn on dark-green cards read `theme.onCardDivider.<palette name>`: solid light beige
// in the new look, the palette unchanged in classic.
const reskinOnCardDividerPalette = {
  ...P,
  ...Object.fromEntries(
    [
      'white',
      'white_op_10',
      'white_op_15',
      'white_op_30',
      'beige_25',
      'beige_25_op_30',
      'beige_50',
      'green_75',
      'green_80'
    ]
      .concat(Object.keys(P).filter((name) => name.startsWith('beige_50_op_') || name.startsWith('green_75_op_')))
      .map((name) => [name, P.beige_60])
  )
};

export const screens = {
  mobile: 500,
  tablet: 768,
  small_desktop: 1024,
  standard_desktop: 1280,
  medium_desktop: 1400,
  big_desktop: 1680
};

export const sizes = {
  headerHeight: 96,
  footerHeight: 100,
  sideColumn: 360,
  contentColumn: 744,
  roundCorners: 6,
  // Callouts and panels nested inside a card.
  innerCorners: 6
};

// Warm beige_90-tinted shadows for the new look's fixed header and footers (quote-fe's footer shadow).
export const surfaceShadows = {
  header: '0 2px 8px rgba(112, 101, 83, 0.08)',
  footer: '0 -8px 16px rgba(112, 101, 83, 0.1)'
};

// The new look's header is quote's slimmer 72px nav (logo, search, text links, account icon).
const reskinSizes = { ...sizes, headerHeight: 72, roundCorners: 16, innerCorners: 8 };

const reskinFonts = {
  // The `!important` makes sure that using a font explicitly
  // will override any font being inherited from higher up in the tree
  // (which will also be !important), but cannot be overridden by any
  // fonts coming from Material UI. (Ex: if you remove the !important,
  // tooltips from Material UI won't use the font you specify for the
  // selector '*')
  //
  // POC #33148: both slots now resolve to Cabin, matching ui-core. The
  // FrankRuhlLibre key is kept so its one caller (error-dialog) still resolves.
  FrankRuhlLibre: `'Cabin', -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica, Arial, sans-serif !important`,
  LibreFranklin: `'Cabin', -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica, Arial, sans-serif !important`
};

export const themePalettes = {
  legacy: { ...legacy, onCard: legacy.colorPalette, onCardDivider: legacy.colorPalette, sizes },
  reskin: {
    colorPalette: reskinColorPalette,
    colors: reskinColors,
    fonts: reskinFonts,
    onCard: reskinOnCardPalette,
    onCardDivider: reskinOnCardDividerPalette,
    sizes: reskinSizes
  }
};

// Module-level importers get the variant fixed for this page load; MUI styles read `theme.*` instead.
export const { colorPalette, colors, fonts } = themePalettes[getThemeVariant()];
